import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════ */
const T = {
  bg:        '#F8F7F4',
  surface:   '#FFFFFF',
  surface2:  '#F1F0ED',
  border:    'rgba(0,0,0,0.10)',
  borderMd:  'rgba(0,0,0,0.18)',
  txt:       '#1A1916',
  txt2:      '#6B6A65',
  txt3:      '#A3A19C',
  accent:    '#185FA5',
  accentBg:  '#E6F1FB',
  accentTxt: '#0C447C',
  greenBg:   '#EAF3DE',
  greenTxt:  '#27500A',
  amberBg:   '#FAEEDA',
  amberTxt:  '#633806',
  redBg:     '#FCEBEB',
  redTxt:    '#791F1F',
  grayBg:    '#F1F0ED',
  grayTxt:   '#444441',
  font:      "'Segoe UI', system-ui, -apple-system, sans-serif",
};

/* ═══════════════════════════════════════════════════════════
   CLUSTER CONFIG
   Confermato con audit su data_sources.metadata_json ->> 'Profilo
   professionale' (dropdown su Altamira, 19 valori puliti + ~33% vuoto
   dovuto a schede candidato storiche/incomplete, non a un bug del
   campo). È un attributo del CANDIDATO, non dell'annuncio — per
   questo un candidato mantiene lo stesso cluster indipendentemente
   da quale annuncio del run sta coprendo.
   Daniele deve esporre la colonna nella vista DB come
   "profilo_professionale" (vedi CREATE OR REPLACE VIEW concordato);
   multifitting_api.py deve includerla nel payload di /nightly.
   Finché il campo non arriva (key assente da TUTTE le righe), la UI
   mostra un piccolo avviso per sviluppatori; se invece arriva ma è
   vuoto per un candidato specifico, è un caso atteso (scheda
   incompleta) e va mostrato come categoria normale, senza allarmi.
═══════════════════════════════════════════════════════════ */
const CLUSTER_FIELD = 'profilo_professionale';
const CLUSTER_UNSET_LABEL = 'Non classificato — scheda incompleta';

/* ═══════════════════════════════════════════════════════════
   MAPPATURA PROFILI CONSIP
   Fonte: tabella DB consip_profile_embeddings (27 profili,
   fonte ufficiale usata per il matching semantico) — NON il
   capitolato PDF, che è più esteso ma statico/non collegato al
   sistema. Chiave = valore esatto di profilo_professionale.
   Valore = slug in consip_profile_embeddings + flag approx per
   i match non testualmente esatti (da validare con Giuseppe/SB).
   Profili candidato assenti da questa mappa (Manager, IT Service
   Manager, Service Desk Specialist, Digital Media Consultant,
   ICT Junior Consultant, Stagista/Neolaureato, HR Consultant,
   Società) non hanno un profilo corrispondente nella tabella —
   nessun dato inventato per coprirli.
═══════════════════════════════════════════════════════════ */
const CONSIP_SLUG_MAP = {
  'PMO o Project Manager':               { slug: 'project_manager',        approx: false },
  'ICT Senior Consultant':                { slug: 'ict_consultant_senior',  approx: false },
  'Developer':                            { slug: 'developer',              approx: false },
  'System Administrator':                 { slug: 'sysadmin',               approx: false },
  'UX UI Designer':                       { slug: 'ux_designer',            approx: false },
  'Testing Specialist':                   { slug: 'test_specialist',        approx: false },
  'DBA':                                  { slug: 'database_specialist',    approx: false },
  'Analyst':                              { slug: 'business_analyst',       approx: true  },
  'Solution & Infrastructure Architect':  { slug: 'enterprise_architect',   approx: true  },
  'AI Expert':                            { slug: 'ai_strategy_advisor',    approx: true  },
  'Business Consultant':                  { slug: 'digital_consultant',     approx: true  },
};

// Scala di qualità del fitting, dal migliore al peggiore. Usata per trovare
// il fitting più alto raggiunto tra un gruppo di candidati (non il più
// frequente — vedi bestFitting()).
const FITTING_RANK = ['OTTIMO', 'BUONO', 'SUFFICIENTE', 'PARZIALE', 'INADEGUATO'];

function bestFitting(rows) {
  const present = new Set(rows.map(r => r.fitting));
  return FITTING_RANK.find(f => present.has(f)) || '—';
}

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
function formatRunDate(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    }).format(new Date(ts));
  } catch { return String(ts).slice(0, 10); }
}

// Formatta un intervallo [minDate, maxDate] di run_date. Se le due date
// coincidono (stesso giorno), mostra solo una data invece di un intervallo
// ridondante — utile ora che i dati non sono più filtrati su un run singolo
// ma su un periodo che può contenere una o più date di run diverse.
function formatDateRange(minTs, maxTs) {
  if (!minTs && !maxTs) return '—';
  const minLabel = formatDateLabel(minTs);
  const maxLabel = formatDateLabel(maxTs);
  if (minLabel === maxLabel) return minLabel;
  return `${minLabel} – ${maxLabel}`;
}

function formatDateLabel(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      timeZone: 'Europe/Rome',
      day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(new Date(dateStr));
  } catch { return dateStr; }
}

// Estrae testo semplice da un campo HTML (es. job_text da Altamira),
// usato per anteprime troncate dove non vogliamo renderizzare markup.
function htmlToText(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

// Sanificazione minima prima di iniettare HTML nel DOM (dangerouslySetInnerHTML):
// rimuove script, handler inline (onClick=...) e URL javascript:. Il contenuto
// proviene da job_text su Altamira (interno, non user-generated), quindi è una
// protezione difensiva aggiuntiva, non l'unica barriera.
function sanitizeJobHtml(html) {
  if (!html) return '';
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript:/gi, '');
}

function avatarColors(name) {
  const colors = [
    ['#E6F1FB','#0C447C'],['#EAF3DE','#27500A'],['#FAEEDA','#633806'],
    ['#FCEBEB','#791F1F'],['#F1F0ED','#444441'],['#E8F5FF','#1A5FA8'],
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return colors[h % colors.length];
}

function Avatar({ name, size = 30 }) {
  const initials = name ? name.split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase() : '?';
  const [bg, fg] = avatarColors(name || '');
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: 600, flexShrink: 0,
    }}>{initials}</div>
  );
}

function ScoreFill({ score }) {
  const color = score >= 80 ? T.accent : score >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, minWidth:60, height:5, borderRadius:3, background:T.surface2, border:`0.5px solid ${T.border}` }}>
        <div style={{ width:`${score}%`, height:'100%', borderRadius:3, background: color, transition:'width 0.3s' }} />
      </div>
      <span style={{ fontSize:13, fontWeight:600, minWidth:24 }}>{score}</span>
    </div>
  );
}

function FittingBadge({ fitting }) {
  const map = {
    OTTIMO:      [T.greenBg,  T.greenTxt],
    BUONO:       ['#d4edda',  '#155724'],
    SUFFICIENTE: [T.amberBg,  T.amberTxt],
    PARZIALE:    ['#fde8d8',  '#7d3b00'],
    INADEGUATO:  [T.redBg,    T.redTxt],
  };
  const [bg, fg] = map[fitting] || [T.grayBg, T.grayTxt];
  return (
    <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:bg, color:fg, whiteSpace:'nowrap' }}>
      {fitting || '—'}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    OK:     [T.greenBg,  T.greenTxt,  'OK'],
    REUSED: [T.accentBg, T.accentTxt, 'Storico'],
    ERROR:  [T.redBg,    T.redTxt,    'Errore'],
  };
  const [bg, fg, lbl] = map[status] || [T.grayBg, T.grayTxt, status];
  return <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:bg, color:fg }}>{lbl}</span>;
}

function Rank({ n }) {
  const styles = {
    1: { background:'#FAEEDA', border:'0.5px solid #EF9F27', color:'#633806' },
    2: { background:'#F1F0ED', border:`0.5px solid #B4B2A9`, color:'#3C3A36' },
    3: { background:'#FAECE7', border:'0.5px solid #F0997B', color:'#712B13' },
  };
  const s = styles[n] || { background:T.surface2, border:`0.5px solid ${T.border}`, color:T.txt2 };
  return (
    <div style={{ width:24, height:24, borderRadius:'50%', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, ...s }}>{n}</div>
  );
}

/* ═══════════════════════════════════════════════════════════
   EXPORT MENU
   Bottone unico con dropdown formato (xlsx/csv/json). Chiama
   /api/multifitting/export con i filtri della vista corrente
   (passati via buildParams) e scarica il blob risultante.
═══════════════════════════════════════════════════════════ */
function ExportMenu({ buildParams }) {
  const [open, setOpen]             = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function doExport(format) {
    setOpen(false);
    setDownloading(true);
    try {
      const params = new URLSearchParams({ format });
      const raw = buildParams() || {};
      Object.entries(raw).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.set(k, v);
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/multifitting/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Errore export (HTTP ${response.status})`);

      const blob = await response.blob();
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `multifitting_export.${format}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Errore durante l'export: " + err.message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div style={{ position:'relative' }}>
      <button onClick={() => setOpen(o => !o)} disabled={downloading}
        style={{ height:32, padding:'0 12px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor: downloading ? 'default' : 'pointer', fontFamily:T.font, display:'flex', alignItems:'center', gap:6 }}>
        {downloading ? '⏳ Esportazione…' : <>⬇ Esporta <span style={{ fontSize:10 }}>▾</span></>}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:300 }} />
          <div style={{ position:'absolute', top:36, right:0, zIndex:301, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', minWidth:150, overflow:'hidden' }}>
            {[['xlsx', '📊 Excel (.xlsx)'], ['csv', '📄 CSV'], ['jsonl', '🔧 JSONL']].map(([fmt, lbl]) => (
              <div key={fmt} onClick={() => doExport(fmt)}
                style={{ padding:'9px 14px', fontSize:13, cursor:'pointer', fontFamily:T.font, whiteSpace:'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >{lbl}</div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const PAGE_SIZE = 15;

/* ═══════════════════════════════════════════════════════════
   SIDE PANEL
═══════════════════════════════════════════════════════════ */
function SidePanel({ candidate, allRows, onClose }) {
  const crossFit = allRows
    .filter(r => r.cvid === candidate.cvid)
    .sort((a, b) => b.hybrid_score - a.hybrid_score);

  const reqPresent = Array.isArray(candidate.req_present) ? candidate.req_present : [];
  const reqMissing = Array.isArray(candidate.req_missing) ? candidate.req_missing : [];

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:200 }} />
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:400, zIndex:201,
        background:T.surface, overflowY:'auto',
        boxShadow:'-4px 0 24px rgba(0,0,0,0.12)',
        fontFamily:T.font, fontSize:14
      }}>
        <div style={{ padding:'16px 18px', borderBottom:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:T.surface, zIndex:1 }}>
          <h3 style={{ fontSize:14, fontWeight:600 }}>Dettaglio candidato</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:T.txt2, fontSize:18, lineHeight:1 }}>✕</button>
        </div>

        <div style={{ paddingBottom:32 }}>
          {/* Identity */}
          <div style={{ padding:'16px 18px', borderBottom:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:10 }}>
            <Avatar name={candidate.candidate_name} size={40} />
            <div>
              <div style={{ fontSize:15, fontWeight:600 }}>{candidate.candidate_name}</div>
              <div style={{ fontSize:12, color:T.txt2 }}>{candidate.competenza_principale || '—'}</div>
            </div>
          </div>

          {/* Score */}
          <div style={{ padding:'14px 18px', borderBottom:`0.5px solid ${T.border}` }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:7 }}>
              <span style={{ fontSize:32, fontWeight:700, lineHeight:1 }}>{candidate.hybrid_score}</span>
              <div>
                <div style={{ fontSize:11, color:T.txt3 }}>Hybrid Score</div>
                <FittingBadge fitting={candidate.fitting} />
              </div>
            </div>
            <ScoreFill score={candidate.hybrid_score} />
            <div style={{ display:'flex', gap:12, marginTop:8, fontSize:12, color:T.txt2 }}>
              <span>LLM: <strong>{candidate.llm_score}</strong></span>
              <span>Cosine: <strong>{candidate.cosine_norm}</strong></span>
              <StatusBadge status={candidate.status || 'REUSED'} />
            </div>
          </div>

          {/* Contatti */}
          <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Contatti</div>
            <div style={{ fontSize:13, color:T.txt2 }}>✉ {candidate.candidate_email || '—'}</div>
          </div>

          {/* Competenze */}
          <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Competenze</div>
            <div style={{ fontSize:13 }}>Principale: <strong>{candidate.competenza_principale || '—'}</strong></div>
            <div style={{ fontSize:13, color:T.txt2, marginTop:2 }}>Secondaria: {candidate.competenza_secondaria || '—'}</div>
            {candidate.certificazioni && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:8 }}>
                {candidate.certificazioni.split(',').map((c, i) => (
                  <span key={i} style={{ fontSize:11, padding:'3px 8px', borderRadius:12, background:T.surface2, border:`0.5px solid ${T.border}`, color:T.txt2 }}>{c.trim()}</span>
                ))}
              </div>
            )}
          </div>

          {/* Logistica & Costi */}
          <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Logistica & Costi</div>
            <div style={{ fontSize:13, marginBottom:4 }}>📍 {candidate.localita || '—'}</div>
            {(candidate.costo_onsite || candidate.costo_remoto) && (
              <div style={{ fontSize:13, marginBottom:4 }}>💶 On-site: <strong>{candidate.costo_onsite || '—'}</strong> · Remote: {candidate.costo_remoto || '—'}</div>
            )}
            {candidate.disponibilita && (
              <div style={{ fontSize:12, color:T.txt2 }}>🕐 {candidate.disponibilita}</div>
            )}
          </div>

          {/* Voto CV */}
          {candidate.voto_cv && (
            <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
              <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Voto CV</div>
              <span style={{ padding:'3px 10px', borderRadius:12, fontSize:12, background:T.accentBg, color:T.accentTxt, fontWeight:500 }}>{candidate.voto_cv}</span>
            </div>
          )}

          {/* Reasoning */}
          {candidate.reasoning && (
            <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
              <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>🧠 Ragionamento AI</div>
              <div style={{ fontSize:12, lineHeight:1.6, color:T.txt2 }}>{candidate.reasoning}</div>
            </div>
          )}

          {/* Requisiti presenti */}
          {reqPresent.length > 0 && (
            <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
              <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Requisiti presenti</div>
              {reqPresent.map((r, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, marginBottom:4 }}>
                  <span style={{ color:T.greenTxt }}>✓</span><span>{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* Requisiti assenti */}
          <div style={{ padding:'12px 18px', borderBottom:`0.5px solid ${T.border}` }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>Requisiti assenti</div>
            {reqMissing.length > 0
              ? reqMissing.map((r, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, marginBottom:4 }}>
                    <span style={{ color:T.redTxt }}>✗</span><span>{r}</span>
                  </div>
                ))
              : <span style={{ fontSize:12, color:T.txt3 }}>Nessuno — fit completo</span>
            }
          </div>

          {/* Cross-fit */}
          <div style={{ padding:'12px 18px' }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>
              📈 Fitting su tutti gli annunci del run
            </div>
            {candidate.source_job_name && (
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 10px', borderRadius:6, background:T.greenBg, marginBottom:12 }}>
                <span style={{ fontSize:13 }}>📌</span>
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:T.greenTxt, textTransform:'uppercase', letterSpacing:'0.05em' }}>Candidatura originale</div>
                  <div style={{ fontSize:12, fontWeight:500, color:T.greenTxt, marginTop:1 }}>{candidate.source_job_name}</div>
                </div>
              </div>
            )}
            {crossFit.map((r, i) => {
              const isCurrent = r.target_job_id === candidate.target_job_id;
              const barColor = r.hybrid_score >= 75 ? T.accent : r.hybrid_score >= 50 ? '#F59E0B' : '#EF4444';
              return (
                <div key={i} style={{ marginBottom:7, padding:'6px 8px', borderRadius:6, background: isCurrent ? T.accentBg : 'transparent' }}>
                  <div style={{ fontSize:11, color: isCurrent ? T.accentTxt : T.txt2, fontWeight: isCurrent ? 600 : 400, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {isCurrent ? '▶ ' : ''}{r.target_job_name}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ flex:1, height:4, borderRadius:2, background:T.surface2 }}>
                      <div style={{ width:`${r.hybrid_score}%`, height:'100%', borderRadius:2, background: isCurrent ? T.accent : barColor }} />
                    </div>
                    <span style={{ fontSize:11, fontWeight:600, minWidth:22, color:T.txt2 }}>{r.hybrid_score}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Cluster (raggruppamento per profilo professionale del CANDIDATO)
═══════════════════════════════════════════════════════════ */
function ScreenCluster({ rows, consipProfiles, onSelectCluster }) {
  const [search, setSearch] = useState('');
  const [sort, setSort]     = useState('cand_desc');

  // Distingue "il backend non manda ancora il campo" (key assente su TUTTE le righe,
  // problema di integrazione) da "il candidato non ha compilato il profilo" (valore
  // vuoto/null su alcune righe, ~33% atteso — vedi audit su data_sources, non è un bug).
  const fieldNotYetShipped = rows.length > 0 && rows.every(r => r[CLUSTER_FIELD] === undefined);

  // Dedupe per candidato: un cvid può comparire su più annunci nello stesso run.
  // Il profilo professionale è un attributo del candidato (stabile), quindi si tiene
  // una sola riga per cvid, con lo score migliore ottenuto nel run.
  const bestByCvid = {};
  rows.forEach(r => {
    const prev = bestByCvid[r.cvid];
    if (!prev || (r.hybrid_score ?? 0) > (prev.hybrid_score ?? 0)) bestByCvid[r.cvid] = r;
  });

  const byCluster = {};
  Object.values(bestByCvid).forEach(r => {
    const raw = r[CLUSTER_FIELD];
    const key = raw && raw.trim() ? raw : CLUSTER_UNSET_LABEL;
    if (!byCluster[key]) byCluster[key] = { id: key, title: key, cands: new Set(), scores: [], minRunDate: r.run_date, maxRunDate: r.run_date };
    byCluster[key].cands.add(r.cvid);
    if (typeof r.hybrid_score === 'number') byCluster[key].scores.push(r.hybrid_score);
    if (r.run_date && (!byCluster[key].minRunDate || r.run_date < byCluster[key].minRunDate)) byCluster[key].minRunDate = r.run_date;
    if (r.run_date && (!byCluster[key].maxRunDate || r.run_date > byCluster[key].maxRunDate)) byCluster[key].maxRunDate = r.run_date;
  });

  let clusters = Object.values(byCluster).map(c => ({
    ...c,
    candCount: c.cands.size,
    scoreAvg: c.scores.length ? Math.round(c.scores.reduce((a, b) => a + b, 0) / c.scores.length) : 0,
  }));
  if (search) clusters = clusters.filter(c => c.title.toLowerCase().includes(search.toLowerCase()));
  if (sort === 'cand_desc')  clusters.sort((a, b) => b.candCount - a.candCount);
  if (sort === 'score_desc') clusters.sort((a, b) => b.scoreAvg - a.scoreAvg);
  if (sort === 'title_asc')  clusters.sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:600, letterSpacing:'-0.02em' }}>Cluster</h1>
        <p style={{ fontSize:13, color:T.txt2, marginTop:3 }}>Candidati raggruppati per profilo professionale.</p>
      </div>

      {fieldNotYetShipped && (
        <div style={{ padding:'12px 16px', borderRadius:10, background:T.amberBg, color:T.amberTxt, fontSize:13, marginBottom:16 }}>
          ⚠️ Il campo <code>{CLUSTER_FIELD}</code> non è ancora presente nel payload di <code>/api/multifitting/nightly</code>.
          Verificare che la vista DB e <code>multifitting_api.py</code> siano stati aggiornati (vedi nota CLUSTER CONFIG in cima al file).
        </div>
      )}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca cluster…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Ordina per</span>
          <select value={sort} onChange={e => setSort(e.target.value)}
            style={{ height:32, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, background:T.surface, fontFamily:T.font }}>
            <option value="cand_desc">N. candidati ↓</option>
            <option value="score_desc">Score medio ↓</option>
            <option value="title_asc">Titolo A→Z</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize:12, color:T.txt3, marginBottom:12 }}>{clusters.length} di {Object.keys(byCluster).length} cluster</div>

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {clusters.map(c => {
          const isUnset = c.id === CLUSTER_UNSET_LABEL;
          return (
            <div key={c.id} onClick={() => onSelectCluster(c)}
              style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', cursor:'pointer', transition:'border-color 0.13s, box-shadow 0.13s', opacity: isUnset ? 0.75 : 1 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderMd; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:6, background: isUnset ? T.grayBg : T.accentBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:16 }}>{isUnset ? '❔' : '🗂️'}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:600, marginBottom:3, fontStyle: isUnset ? 'italic' : 'normal', color: isUnset ? T.txt2 : T.txt }}>{c.title}</div>
                  <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background: isUnset ? T.grayBg : T.accentBg, color: isUnset ? T.grayTxt : T.accentTxt }}>Score medio: {c.scoreAvg}</span>
                  {(() => {
                    const mapping = CONSIP_SLUG_MAP[c.id];
                    const profile = mapping && consipProfiles.find(p => p.profile_slug === mapping.slug);
                    if (profile) {
                      return (
                        <div style={{ fontSize:12, color:T.txt2, marginTop:6, lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                          {profile.profile_text}
                        </div>
                      );
                    }
                    if (isUnset) {
                      return (
                        <div style={{ fontSize:12, color:T.txt3, marginTop:6, fontStyle:'italic' }}>
                          Campo "Profilo professionale" non compilato su Altamira — non è un errore di sistema
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, marginLeft:24 }}>
                  <span style={{ fontSize:28, fontWeight:700, lineHeight:1 }}>{c.candCount}</span>
                  <span style={{ fontSize:11, color:T.txt3, marginTop:2 }}>candidati</span>
                </div>
              </div>
            </div>
          );
        })}
        {clusters.length === 0 && <div style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>Nessun cluster trovato</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Candidati per Cluster
   Un candidato può comparire su più annunci dello stesso cluster:
   si mostra una sola riga per candidato, con lo score migliore
   ottenuto all'interno del cluster. Il dettaglio (SidePanel) mostra
   comunque il fitting su tutti gli annunci del run.
═══════════════════════════════════════════════════════════ */
function ScreenCandidatiCluster({ rows, cluster, startDate, endDate, consipProfiles, onBack, onOpenPanel }) {
  const [search, setSearch]       = useState('');
  const [minScore, setMinScore]   = useState('');
  const [sortField, setSortField] = useState('hybrid_score');
  const [sortDir, setSortDir]     = useState('desc');
  const [page, setPage]           = useState(1);
  const [consipExpanded, setConsipExpanded] = useState(false);

  const clusterRows = rows.filter(r => {
    const raw = r[CLUSTER_FIELD];
    const key = raw && raw.trim() ? raw : CLUSTER_UNSET_LABEL;
    return key === cluster.id;
  });

  // Dedupe per candidato: tiene la riga con lo score più alto nel cluster
  const bestByCand = {};
  clusterRows.forEach(r => {
    const prev = bestByCand[r.cvid];
    if (!prev || (r.hybrid_score ?? 0) > (prev.hybrid_score ?? 0)) bestByCand[r.cvid] = r;
  });
  const annRows = Object.values(bestByCand);

  const scores  = annRows.map(r => r.hybrid_score).filter(Boolean);
  const scoreAvg = scores.length ? Math.round(scores.reduce((a,b) => a+b, 0) / scores.length) : 0;
  const scoreMax = scores.length ? Math.max(...scores) : 0;
  const topFitting = bestFitting(annRows);

  let filtered = [...annRows];
  if (search) filtered = filtered.filter(r =>
    (r.candidate_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.localita||'').toLowerCase().includes(search.toLowerCase())
  );
  if (minScore) filtered = filtered.filter(r => r.hybrid_score >= parseInt(minScore));
  filtered.sort((a, b) => {
    const va = a[sortField] ?? '', vb = b[sortField] ?? '';
    return sortDir === 'desc' ? (vb > va ? 1 : -1) : (va > vb ? 1 : -1);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows   = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.txt3, marginBottom:18 }}>
        <span onClick={onBack} style={{ color:T.accent, cursor:'pointer' }}>← Tutti i cluster</span>
        <span>›</span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:400 }}>{cluster.title}</span>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', marginBottom:16 }}>
        <h2 style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>{cluster.title}</h2>
        <div style={{ fontSize:12, color:T.txt3 }}>Run: {formatDateRange(cluster.minRunDate, cluster.maxRunDate)}</div>
        {(() => {
          const mapping = CONSIP_SLUG_MAP[cluster.id];
          const profile = mapping && consipProfiles.find(p => p.profile_slug === mapping.slug);
          if (profile) {
            return (
              <div style={{ marginTop:12, paddingTop:12, borderTop:`0.5px solid ${T.border}` }}>
                <div style={{ fontSize:11, fontWeight:600, color:T.txt3, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>
                  📋 Profilo Consip: {profile.profile_titolo}
                </div>
                <div style={{
                  fontSize:13, color:T.txt2, lineHeight:1.6, whiteSpace:'pre-wrap',
                  maxHeight: consipExpanded ? 340 : 90,
                  overflowY: consipExpanded ? 'auto' : 'hidden',
                  paddingRight: consipExpanded ? 8 : 0,
                }}>
                  {profile.profile_text}
                </div>
                <button onClick={() => setConsipExpanded(v => !v)}
                  style={{ marginTop:6, padding:0, border:'none', background:'none', color:T.accent, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:T.font }}>
                  {consipExpanded ? '▲ Mostra meno' : '▼ Mostra descrizione completa'}
                </button>
                <div style={{ fontSize:10, color:T.txt3, marginTop:8 }}>Fonte: tabella consip_profile_embeddings</div>
              </div>
            );
          }
          if (cluster.id === CLUSTER_UNSET_LABEL) {
            return (
              <div style={{ marginTop:12, paddingTop:12, borderTop:`0.5px solid ${T.border}`, display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color:T.txt2 }}>
                <span style={{ fontSize:14, flexShrink:0 }}>ℹ️</span>
                <span>Il campo <strong>"Profilo professionale"</strong> non è compilato su Altamira per questi candidati (schede storiche o incomplete). Non è un errore della dashboard né della pipeline di scoring.</span>
              </div>
            );
          }
          return null;
        })()}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:16 }}>
        {[['Candidati nel cluster', cluster.candCount], ['Score medio', scoreAvg], ['Score massimo', scoreMax], ['Miglior fitting', topFitting]].map(([lbl, val]) => (
          <div key={lbl} style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:22, fontWeight:600, lineHeight:1.1 }}>{val}</div>
            <div style={{ fontSize:11, color:T.txt3, marginTop:3 }}>{lbl}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca candidato…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Score min</span>
          <input type="number" min="0" max="100" placeholder="0" value={minScore} onChange={e => { setMinScore(e.target.value); setPage(1); }}
            style={{ height:32, width:64, padding:'0 8px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:T.txt3 }}>{filtered.length} candidato{filtered.length !== 1 ? 'i' : ''} valutati</span>
          <ExportMenu buildParams={() => ({
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            cluster: cluster.id === CLUSTER_UNSET_LABEL ? '__UNSET__' : cluster.id,
            min_score: minScore || undefined,
            search: search || undefined,
          })} />
        </div>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:T.surface2 }}>
              {[['#','',36],['Candidato','candidate_name',null],['Score','hybrid_score',null],['Fitting','fitting',null],['Regione','localita',null],['Stato','status',null],['','',40]].map(([h,f,w]) => (
                <th key={h} onClick={() => f && toggleSort(f)}
                  style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color: sortField===f ? T.accent : T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', cursor:f?'pointer':'default', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>
                  {h}{sortField===f ? (sortDir==='desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={7} style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>Nessun candidato trovato</td></tr>
            )}
            {pageRows.map((r, i) => {
              const globalRank = (page-1)*PAGE_SIZE + i + 1;
              return (
                <tr key={i} onClick={() => onOpenPanel(r)}
                  style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: r.status === 'ERROR' ? '#fff9f9' : T.surface }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = r.status === 'ERROR' ? '#fff9f9' : T.surface}
                >
                  <td style={{ padding:'11px 12px' }}><Rank n={globalRank} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={r.candidate_name} />
                      <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', minWidth:120 }}><ScoreFill score={r.hybrid_score} /></td>
                  <td style={{ padding:'11px 12px' }}><FittingBadge fitting={r.fitting} /></td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                  <td style={{ padding:'11px 12px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                      style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ padding:'10px 16px', borderTop:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:6, justifyContent:'center' }}>
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===1?'not-allowed':'pointer', opacity: page===1?0.5:1, fontFamily:T.font }}>‹</button>
            <span style={{ fontSize:12, color:T.txt2 }}>Pag. {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===totalPages?'not-allowed':'pointer', opacity: page===totalPages?0.5:1, fontFamily:T.font }}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Annunci
═══════════════════════════════════════════════════════════ */
function ScreenAnnunci({ rows, onSelectAnn }) {
  const [search, setSearch] = useState('');
  const [sort, setSort]     = useState('cand_desc');

  const byJob = {};
  rows.forEach(r => {
    if (!byJob[r.target_job_id]) {
      byJob[r.target_job_id] = { id: r.target_job_id, title: r.target_job_name, description: r.target_job_description, cands: new Set(), minRunDate: r.run_date, maxRunDate: r.run_date };
    }
    byJob[r.target_job_id].cands.add(r.cvid);
    if (r.run_date && (!byJob[r.target_job_id].minRunDate || r.run_date < byJob[r.target_job_id].minRunDate)) byJob[r.target_job_id].minRunDate = r.run_date;
    if (r.run_date && (!byJob[r.target_job_id].maxRunDate || r.run_date > byJob[r.target_job_id].maxRunDate)) byJob[r.target_job_id].maxRunDate = r.run_date;
  });

  // Job che ricevono candidature dirette reali ma non sono mai state scelte
  // come target di cross-fit da nessun candidato del periodo: senza questo
  // passaggio sparirebbero del tutto dall'elenco, pur essendo annunci reali
  // e pubblicati (es. "Gartner - Junior AI Engineer": 242 candidature dirette,
  // 0 volte target). Restano con candCount=0 nel pool, badge esplicito in card.
  const targetIds = new Set(Object.keys(byJob));
  rows.forEach(r => {
    if (!r.source_job_id || targetIds.has(r.source_job_id)) return;
    if (!byJob[r.source_job_id]) {
      byJob[r.source_job_id] = {
        id: r.source_job_id, title: r.source_job_name || r.source_job_id,
        description: undefined, cands: new Set(),
        minRunDate: r.run_date, maxRunDate: r.run_date, noCrossFit: true,
      };
    }
    const j = byJob[r.source_job_id];
    if (r.run_date && (!j.minRunDate || r.run_date < j.minRunDate)) j.minRunDate = r.run_date;
    if (r.run_date && (!j.maxRunDate || r.run_date > j.maxRunDate)) j.maxRunDate = r.run_date;
  });

  let jobs = Object.values(byJob).map(j => ({ ...j, candCount: j.cands.size }));
  if (search) jobs = jobs.filter(j => j.title.toLowerCase().includes(search.toLowerCase()));
  if (sort === 'cand_desc') jobs.sort((a,b) => b.candCount - a.candCount);
  if (sort === 'title_asc') jobs.sort((a,b) => a.title.localeCompare(b.title));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:600, letterSpacing:'-0.02em' }}>Annunci</h1>
        <p style={{ fontSize:13, color:T.txt2, marginTop:3 }}>Job description attive nel run — fonte: Altamira HRM</p>
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca annuncio…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Ordina per</span>
          <select value={sort} onChange={e => setSort(e.target.value)}
            style={{ height:32, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, background:T.surface, fontFamily:T.font }}>
            <option value="cand_desc">N. candidati ↓</option>
            <option value="title_asc">Titolo A→Z</option>
          </select>
        </div>
      </div>

      <div style={{ fontSize:12, color:T.txt3, marginBottom:12 }}>{jobs.length} di {Object.keys(byJob).length} annunci</div>

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {jobs.map(jd => (
          <div key={jd.id} onClick={() => onSelectAnn(jd)}
            style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', cursor:'pointer', transition:'border-color 0.13s, box-shadow 0.13s', opacity: jd.noCrossFit ? 0.85 : 1 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderMd; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:38, height:38, borderRadius:6, background: jd.noCrossFit ? T.grayBg : T.accentBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <span style={{ fontSize:16 }}>{jd.noCrossFit ? '📥' : '💼'}</span>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:600, marginBottom:3 }}>{jd.title}</div>
                <div style={{ fontSize:12, color:T.txt3 }}>Run: {formatDateRange(jd.minRunDate, jd.maxRunDate)}</div>
                {jd.description && (
                  <div style={{ fontSize:12, color:T.txt2, marginTop:6, lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                    {htmlToText(jd.description)}
                  </div>
                )}
                {jd.noCrossFit && (
                  <div style={{ fontSize:11, color:T.grayTxt, background:T.grayBg, display:'inline-block', padding:'2px 8px', borderRadius:12, marginTop:6 }}>
                    Nessun cross-fit disponibile — solo candidature dirette
                  </div>
                )}
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, marginLeft:24 }}>
                <span style={{ fontSize:28, fontWeight:700, lineHeight:1, color: jd.noCrossFit ? T.txt3 : T.txt }}>{jd.candCount}</span>
                <span style={{ fontSize:11, color:T.txt3, marginTop:2 }}>candidati</span>
              </div>
            </div>
          </div>
        ))}
        {jobs.length === 0 && <div style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>Nessun annuncio trovato</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SCREEN: Candidati per Annuncio
═══════════════════════════════════════════════════════════ */
function ScreenCandidatiAnn({ rows, ann, startDate, endDate, onBack, onOpenPanel }) {
  const [search, setSearch]       = useState('');
  const [minScore, setMinScore]   = useState('');
  const [sortField, setSortField] = useState('hybrid_score');
  const [sortDir, setSortDir]     = useState('desc');
  const [page, setPage]           = useState(1);
  const [descExpanded, setDescExpanded] = useState(false);

  const [directRows, setDirectRows]         = useState([]);
  const [directLoading, setDirectLoading]   = useState(false);

  const [historicalRows, setHistoricalRows]     = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalSearch, setHistoricalSearch]   = useState('');
  const [historicalMinScore, setHistoricalMinScore] = useState('');

  useEffect(() => {
    if (!ann?.id) return;
    setDirectLoading(true);
    axios.get(`/api/multifitting/direct/${ann.id}`)
      .then(r => setDirectRows(r.data || []))
      .catch(() => setDirectRows([]))
      .finally(() => setDirectLoading(false));
  }, [ann.id]);

  useEffect(() => {
    if (!ann?.id) return;
    setHistoricalLoading(true);
    axios.get(`/api/multifitting/historical/${ann.id}`)
      .then(r => setHistoricalRows(r.data || []))
      .catch(() => setHistoricalRows([]))
      .finally(() => setHistoricalLoading(false));
  }, [ann.id]);

  const annRows = rows.filter(r => r.target_job_id === ann.id);
  const scores  = annRows.map(r => r.hybrid_score).filter(Boolean);
  const scoreAvg = scores.length ? Math.round(scores.reduce((a,b) => a+b, 0) / scores.length) : 0;
  const scoreMax = scores.length ? Math.max(...scores) : 0;
  const topFitting = bestFitting(annRows);

  let filtered = [...annRows];
  if (search) filtered = filtered.filter(r =>
    (r.candidate_name||'').toLowerCase().includes(search.toLowerCase()) ||
    (r.localita||'').toLowerCase().includes(search.toLowerCase())
  );
  if (minScore) filtered = filtered.filter(r => r.hybrid_score >= parseInt(minScore));
  filtered.sort((a, b) => {
    const va = a[sortField] ?? '', vb = b[sortField] ?? '';
    return sortDir === 'desc' ? (vb > va ? 1 : -1) : (va > vb ? 1 : -1);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows   = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  let historicalFiltered = [...historicalRows];
  if (historicalSearch) historicalFiltered = historicalFiltered.filter(r =>
    (r.candidate_name||'').toLowerCase().includes(historicalSearch.toLowerCase()) ||
    (r.localita||'').toLowerCase().includes(historicalSearch.toLowerCase())
  );
  if (historicalMinScore) historicalFiltered = historicalFiltered.filter(r => (r.hybrid_score ?? 0) >= parseInt(historicalMinScore));

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:T.txt3, marginBottom:18 }}>
        <span onClick={onBack} style={{ color:T.accent, cursor:'pointer' }}>← Tutti gli annunci</span>
        <span>›</span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:400 }}>{ann.title}</span>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, padding:'16px 18px', marginBottom:16 }}>
        <h2 style={{ fontSize:16, fontWeight:600, marginBottom:4 }}>{ann.title}</h2>
        <div style={{ fontSize:12, color:T.txt3 }}>Run: {formatDateRange(ann.minRunDate, ann.maxRunDate)}</div>
        {ann.description && (
          <div style={{ marginTop:12, paddingTop:12, borderTop:`0.5px solid ${T.border}` }}>
            <div
              style={{
                fontSize:13, color:T.txt2, lineHeight:1.6,
                maxHeight: descExpanded ? 340 : 90,
                overflowY: descExpanded ? 'auto' : 'hidden',
                overflowX: 'hidden',
                paddingRight: descExpanded ? 8 : 0,
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeJobHtml(ann.description) }}
            />
            <button onClick={() => setDescExpanded(v => !v)}
              style={{ marginTop:6, padding:0, border:'none', background:'none', color:T.accent, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:T.font }}>
              {descExpanded ? '▲ Mostra meno' : '▼ Mostra descrizione completa'}
            </button>
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:24 }}>
        {[['Candidati valutati (pool)', ann.candCount], ['Score medio (pool)', scoreAvg], ['Score massimo (pool)', scoreMax], ['Miglior fitting (pool)', topFitting]].map(([lbl, val]) => (
          <div key={lbl} style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:10, padding:'12px 14px' }}>
            <div style={{ fontSize:22, fontWeight:600, lineHeight:1.1 }}>{val}</div>
            <div style={{ fontSize:11, color:T.txt3, marginTop:3 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Nota di perimetro: le card sopra sintetizzano il pool MultiFitting
          (tutti i candidati del run valutati contro questo annuncio via
          cross-fit), NON le sole candidature dirette ricevute su Altamira —
          vedi punti 2/3 audit dashboard. Le due sezioni sotto restano
          volutamente distinte per evitare di far credere che rappresentino
          lo stesso insieme di candidati. */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'10px 14px', borderRadius:10, background:T.accentBg, color:T.accentTxt, fontSize:12, marginBottom:24 }}>
        <span style={{ fontSize:14, flexShrink:0 }}>ℹ️</span>
        <span>Questi valori si riferiscono al <strong>pool MultiFitting</strong>: tutti i candidati del run valutati contro questo annuncio, non solo chi si è candidato direttamente. Per le candidature effettive ricevute su Altamira, vedi la sezione "Candidature dirette" qui sotto.</span>
      </div>

      {/* ── SEZIONE 1: Candidature dirette ── */}
      <div style={{ marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:12 }}>
          <h3 style={{ fontSize:14, fontWeight:700, margin:0 }}>📥 Candidature dirette</h3>
          {!directLoading && (
            <span style={{ fontSize:12, color:T.txt3 }}>
              {directRows.length} candidat{directRows.length !== 1 ? 'i' : 'o'} · {directRows.filter(r => r.has_score).length} valutati
            </span>
          )}
        </div>

        {directLoading ? (
          <div style={{ padding:'2rem', textAlign:'center', color:T.txt3, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14 }}>Caricamento…</div>
        ) : (
          <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:T.surface2 }}>
                  {[['#',36],['Candidato',null],['Score',null],['Fitting',null],['Regione',null],['Valutazione',null],['',40]].map(([h,w]) => (
                    <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {directRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding:'2rem', textAlign:'center', color:T.txt3 }}>Nessuna candidatura diretta</td></tr>
                )}
                {directRows.map((r, i) => (
                  <tr key={i} onClick={() => onOpenPanel(r)}
                    style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: T.surface }}
                    onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = T.surface}
                  >
                    <td style={{ padding:'11px 12px' }}><Rank n={i + 1} /></td>
                    <td style={{ padding:'11px 12px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <Avatar name={r.candidate_name} />
                        <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                      </div>
                    </td>
                    <td style={{ padding:'11px 12px', minWidth:120 }}>
                      {r.has_score ? <ScoreFill score={r.hybrid_score} /> : <span style={{ color:T.txt3 }}>—</span>}
                    </td>
                    <td style={{ padding:'11px 12px' }}>
                      {r.has_score ? <FittingBadge fitting={r.fitting} /> : <span style={{ color:T.txt3 }}>—</span>}
                    </td>
                    <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                    <td style={{ padding:'11px 12px' }}>
                      {r.has_score
                        ? <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:T.greenBg, color:T.greenTxt }}>Valutato</span>
                        : <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:500, background:T.grayBg, color:T.grayTxt }}>Non ancora valutato</span>
                      }
                    </td>
                    <td style={{ padding:'11px 12px' }}>
                      <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                        style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Separatore ── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, margin:'28px 0 8px' }}>
        <div style={{ flex:1, height:'0.5px', background:T.borderMd }} />
        <h3 style={{ fontSize:13, fontWeight:700, color:T.txt2, margin:0, whiteSpace:'nowrap' }}>🔀 Multi-Fitting pool</h3>
        <div style={{ flex:1, height:'0.5px', background:T.borderMd }} />
      </div>
      <p style={{ fontSize:11, color:T.txt3, textAlign:'center', margin:'0 0 16px' }}>
        Cross-fit sui candidati con nuove candidature nel run: chi si è candidato in precedenza può avere un punteggio già calcolato ma non ancora incluso in questa vista.
      </p>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca candidato…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Score min</span>
          <input type="number" min="0" max="100" placeholder="0" value={minScore} onChange={e => { setMinScore(e.target.value); setPage(1); }}
            style={{ height:32, width:64, padding:'0 8px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:T.txt3 }}>{filtered.length} candidato{filtered.length !== 1 ? 'i' : ''} valutati</span>
          <ExportMenu buildParams={() => ({
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            target_job_id: ann.id,
            min_score: minScore || undefined,
            search: search || undefined,
          })} />
        </div>
      </div>

      <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:T.surface2 }}>
              {[['#','',36],['Candidato','candidate_name',null],['Score','hybrid_score',null],['Fitting','fitting',null],['Regione','localita',null],['Stato','status',null],['','',40]].map(([h,f,w]) => (
                <th key={h} onClick={() => f && toggleSort(f)}
                  style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color: sortField===f ? T.accent : T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', cursor:f?'pointer':'default', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>
                  {h}{sortField===f ? (sortDir==='desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr><td colSpan={7} style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>Nessun candidato trovato</td></tr>
            )}
            {pageRows.map((r, i) => {
              const globalRank = (page-1)*PAGE_SIZE + i + 1;
              return (
                <tr key={i} onClick={() => onOpenPanel(r)}
                  style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: r.status === 'ERROR' ? '#fff9f9' : T.surface }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = r.status === 'ERROR' ? '#fff9f9' : T.surface}
                >
                  <td style={{ padding:'11px 12px' }}><Rank n={globalRank} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={r.candidate_name} />
                      <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', minWidth:120 }}><ScoreFill score={r.hybrid_score} /></td>
                  <td style={{ padding:'11px 12px' }}><FittingBadge fitting={r.fitting} /></td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                  <td style={{ padding:'11px 12px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                      style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ padding:'10px 16px', borderTop:`0.5px solid ${T.border}`, display:'flex', alignItems:'center', gap:6, justifyContent:'center' }}>
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===1?'not-allowed':'pointer', opacity: page===1?0.5:1, fontFamily:T.font }}>‹</button>
            <span style={{ fontSize:12, color:T.txt2 }}>Pag. {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ height:28, padding:'0 10px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor: page===totalPages?'not-allowed':'pointer', opacity: page===totalPages?0.5:1, fontFamily:T.font }}>›</button>
          </div>
        )}
      </div>

      {/* ── SEZIONE 3: Storico proponibili (prototipo) ──
          Punteggi dallo screening massivo iniziale (MAXIFILTER_KEEP_REVIEW,
          1 giugno 2026), non presenti nel pool nightly perché la vista
          v_altamira_nightly_job_multifitting filtra solo su
          ALTAMIRA_NIGHTLY_NEW_APPLICATION. Dato già calcolato, non ricalcolato
          al volo — ma potenzialmente non aggiornato (CV o JD cambiati dopo
          il batch): da qui il badge "Storico" su ogni riga. */}
      <div style={{ display:'flex', alignItems:'center', gap:12, margin:'28px 0 8px' }}>
        <div style={{ flex:1, height:'0.5px', background:T.borderMd }} />
        <h3 style={{ fontSize:13, fontWeight:700, color:T.txt2, margin:0, whiteSpace:'nowrap' }}>📚 Storico proponibili</h3>
        <div style={{ flex:1, height:'0.5px', background:T.borderMd }} />
      </div>
      <p style={{ fontSize:11, color:T.txt3, textAlign:'center', margin:'0 0 12px' }}>
        Punteggi dello screening iniziale (1 giugno 2026) su tutto il pool proponibili, non ancora incluso nel run nightly — dato storico, non ricalcolato di recente.
      </p>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
          <span style={{ position:'absolute', left:9, color:T.txt3, fontSize:13 }}>🔍</span>
          <input type="text" placeholder="Cerca candidato…" value={historicalSearch} onChange={e => setHistoricalSearch(e.target.value)}
            style={{ height:32, paddingLeft:30, paddingRight:10, border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, minWidth:220, outline:'none' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:T.txt2 }}>Score min</span>
          <input type="number" min="0" max="100" placeholder="0" value={historicalMinScore} onChange={e => setHistoricalMinScore(e.target.value)}
            style={{ height:32, width:64, padding:'0 8px', border:`0.5px solid ${T.border}`, borderRadius:6, fontSize:13, fontFamily:T.font, background:T.surface, outline:'none' }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:12, color:T.txt3 }}>{historicalFiltered.length} candidato{historicalFiltered.length !== 1 ? 'i' : ''}</span>
        </div>
      </div>

      {historicalLoading ? (
        <div style={{ padding:'2rem', textAlign:'center', color:T.txt3, background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14 }}>Caricamento…</div>
      ) : (
        <div style={{ background:T.surface, border:`0.5px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:T.surface2 }}>
                {[['#',36],['Candidato',null],['Score',null],['Fitting',null],['Regione',null],['Stato',null],['',40]].map(([h,w]) => (
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:T.txt2, borderBottom:`0.5px solid ${T.border}`, whiteSpace:'nowrap', userSelect:'none', width:w||undefined, letterSpacing:'0.03em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {historicalFiltered.length === 0 && (
                <tr><td colSpan={7} style={{ padding:'2rem', textAlign:'center', color:T.txt3 }}>Nessun punteggio storico disponibile</td></tr>
              )}
              {historicalFiltered.map((r, i) => (
                <tr key={i} onClick={() => onOpenPanel(r)}
                  style={{ borderBottom:`0.5px solid ${T.border}`, cursor:'pointer', transition:'background 0.1s', background: T.surface }}
                  onMouseEnter={e => e.currentTarget.style.background = T.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = T.surface}
                >
                  <td style={{ padding:'11px 12px' }}><Rank n={i + 1} /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={r.candidate_name} />
                      <span style={{ fontWeight:500 }}>{r.candidate_name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', minWidth:120 }}><ScoreFill score={r.hybrid_score} /></td>
                  <td style={{ padding:'11px 12px' }}><FittingBadge fitting={r.fitting} /></td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:T.txt2 }}>{r.localita || '—'}</td>
                  <td style={{ padding:'11px 12px' }}><StatusBadge status="REUSED" /></td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={e => { e.stopPropagation(); onOpenPanel(r); }}
                      style={{ height:26, padding:'0 8px', fontSize:11, border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, cursor:'pointer', fontFamily:T.font }}>👁</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPALE
═══════════════════════════════════════════════════════════ */
export default function MultifittingDashboard() {
  const navigate = useNavigate();

  const [rows, setRows]                   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [view, setView]                   = useState('annunci'); // default: Annunci
  const [selectedAnn, setSelectedAnn]     = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [selectedRow, setSelectedRow]     = useState(null);
  const [startDate, setStartDate]         = useState('');   // '' = nessun filtro
  const [endDate, setEndDate]             = useState('');   // '' = nessun filtro
  const [dateBounds, setDateBounds]       = useState({ min: null, max: null });
  const [consipProfiles, setConsipProfiles] = useState([]);

  // Carica i profili Consip una volta sola (tabella statica, non legata al periodo)
  useEffect(() => {
    axios.get('/api/multifitting/consip-profiles')
      .then(r => setConsipProfiles(r.data || []))
      .catch(() => setConsipProfiles([]));
  }, []);

  // Carica i confini temporali dei dati disponibili (solo per i limiti min/max
  // dei date picker, non più per un selettore a tendina)
  useEffect(() => {
    axios.get('/api/multifitting/runs')
      .then(r => {
        const dates = r.data || []; // ordinate DESC dal backend
        if (dates.length > 0) setDateBounds({ min: dates[dates.length - 1], max: dates[0] });
      })
      .catch(() => {});
  }, []);

  // Fetch dati: default nessun filtro (tutto lo storico). Se è impostata solo
  // la data di inizio, la fine è implicitamente oggi (stessa regola applicata
  // anche lato backend, qui solo per coerenza visiva dei filtri mostrati).
  const effectiveEndDate = (startDate && !endDate) ? new Date().toISOString().slice(0, 10) : endDate;

  const fetchData = useCallback(() => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (effectiveEndDate) params.end_date = effectiveEndDate;
    setLoading(true);
    setError(null);
    axios.get('/api/multifitting/nightly', { params })
      .then(r => setRows(r.data || []))
      .catch(e => {
        if (e.response?.status === 403) navigate('/');
        else setError('Errore nel caricamento dei dati. Riprova.');
      })
      .finally(() => setLoading(false));
  }, [startDate, effectiveEndDate, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleStartDateChange(newDate) {
    setStartDate(newDate);
    setSelectedAnn(null);
    setSelectedCluster(null);
    setView('annunci');
  }

  function handleEndDateChange(newDate) {
    setEndDate(newDate);
    setSelectedAnn(null);
    setSelectedCluster(null);
    setView('annunci');
  }

  function handleResetDates() {
    setStartDate('');
    setEndDate('');
    setSelectedAnn(null);
    setSelectedCluster(null);
    setView('annunci');
  }

  function handleSelectAnn(ann) {
    setSelectedAnn(ann);
    setView('ann-cand');
    window.scrollTo(0, 0);
  }

  function handleBack() {
    setSelectedAnn(null);
    setView('annunci');
    setSelectedRow(null);
    window.scrollTo(0, 0);
  }

  function handleSelectCluster(cluster) {
    setSelectedCluster(cluster);
    setView('cluster-cand');
    window.scrollTo(0, 0);
  }

  function handleBackCluster() {
    setSelectedCluster(null);
    setView('cluster');
    setSelectedRow(null);
    window.scrollTo(0, 0);
  }

  function switchTopView(target) {
    setSelectedAnn(null);
    setSelectedCluster(null);
    setSelectedRow(null);
    setView(target);
    window.scrollTo(0, 0);
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, fontSize:14, color:T.txt }}>
      {/* Navbar */}
      <nav style={{ background:T.surface, borderBottom:`0.5px solid ${T.border}`, padding:'0 28px', height:52, display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:50 }}>
        <span style={{ fontSize:13, fontWeight:600, letterSpacing:'0.05em', color:T.accent }}>AISA TalentGPT</span>
        <span style={{ color:T.borderMd }}>›</span>
        <span style={{ fontSize:13, color:T.txt2 }}>
          {view === 'cluster'      ? 'MultiFitting · Cluster' :
           view === 'cluster-cand' ? `MultiFitting · ${selectedCluster?.title || ''}` :
           view === 'annunci'      ? 'MultiFitting · Annunci' :
                                      `MultiFitting · ${selectedAnn?.title || ''}`}
        </span>

        {/* Toggle vista Cluster / Annunci */}
        <div style={{ display:'flex', alignItems:'center', gap:2, background:T.surface2, border:`0.5px solid ${T.border}`, borderRadius:6, padding:2 }}>
          <button onClick={() => switchTopView('cluster')}
            style={{
              height:26, padding:'0 12px', borderRadius:4, border:'none', fontSize:12, fontFamily:T.font, cursor:'pointer',
              background: (view==='cluster'||view==='cluster-cand') ? T.surface : 'transparent',
              color: (view==='cluster'||view==='cluster-cand') ? T.txt : T.txt2,
              fontWeight: (view==='cluster'||view==='cluster-cand') ? 600 : 400,
              boxShadow: (view==='cluster'||view==='cluster-cand') ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}>
            🗂️ Cluster
          </button>
          <button onClick={() => switchTopView('annunci')}
            style={{
              height:26, padding:'0 12px', borderRadius:4, border:'none', fontSize:12, fontFamily:T.font, cursor:'pointer',
              background: (view==='annunci'||view==='ann-cand') ? T.surface : 'transparent',
              color: (view==='annunci'||view==='ann-cand') ? T.txt : T.txt2,
              fontWeight: (view==='annunci'||view==='ann-cand') ? 600 : 400,
              boxShadow: (view==='annunci'||view==='ann-cand') ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}>
            📋 Annunci
          </button>
        </div>

        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>

          {/* Selettore periodo */}
          <div style={{ display:'flex', alignItems:'center', gap:6, background:T.surface2, border:`0.5px solid ${T.border}`, borderRadius:6, padding:'0 10px', height:32 }}>
            <span style={{ fontSize:11, color:T.txt3 }}>Dal</span>
            <input type="date" value={startDate} min={dateBounds.min || undefined} max={dateBounds.max || undefined}
              onChange={e => handleStartDateChange(e.target.value)}
              style={{ height:28, border:'none', background:'transparent', fontSize:12, fontFamily:T.font, color:T.txt, cursor:'pointer', outline:'none' }} />
            <span style={{ fontSize:11, color:T.txt3 }}>Al</span>
            <input type="date" value={endDate} min={startDate || dateBounds.min || undefined} max={dateBounds.max || undefined}
              onChange={e => handleEndDateChange(e.target.value)}
              style={{ height:28, border:'none', background:'transparent', fontSize:12, fontFamily:T.font, color:T.txt, cursor:'pointer', outline:'none' }} />
            {(startDate || endDate) && (
              <button onClick={handleResetDates} title="Rimuovi filtro periodo"
                style={{ height:20, width:20, border:'none', background:'none', color:T.txt3, cursor:'pointer', fontSize:14, lineHeight:1, padding:0 }}>✕</button>
            )}
          </div>

          <button onClick={fetchData}
            style={{ height:32, padding:'0 13px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor:'pointer', fontFamily:T.font }}>
            ↻
          </button>
          <button onClick={() => navigate('/')}
            style={{ height:32, padding:'0 13px', border:`0.5px solid ${T.border}`, borderRadius:6, background:T.surface, fontSize:13, cursor:'pointer', fontFamily:T.font }}>
            ← Hub
          </button>
        </div>
      </nav>

      {/* Main */}
      <div style={{ padding:'24px 28px 48px', maxWidth:1100, margin:'0 auto', width:'100%' }}>
        {loading && <div style={{ padding:'4rem', textAlign:'center', color:T.txt3 }}>Caricamento…</div>}
        {!loading && error && (
          <div style={{ padding:'1.5rem', borderRadius:8, background:T.redBg, color:T.redTxt }}>{error}</div>
        )}
        {!loading && !error && view === 'cluster' && (
          <ScreenCluster rows={rows} consipProfiles={consipProfiles} onSelectCluster={handleSelectCluster} />
        )}
        {!loading && !error && view === 'cluster-cand' && selectedCluster && (
          <ScreenCandidatiCluster rows={rows} cluster={selectedCluster} startDate={startDate} endDate={endDate} consipProfiles={consipProfiles} onBack={handleBackCluster} onOpenPanel={setSelectedRow} />
        )}
        {!loading && !error && view === 'annunci' && (
          <ScreenAnnunci rows={rows} onSelectAnn={handleSelectAnn} />
        )}
        {!loading && !error && view === 'ann-cand' && selectedAnn && (
          <ScreenCandidatiAnn rows={rows} ann={selectedAnn} startDate={startDate} endDate={endDate} onBack={handleBack} onOpenPanel={setSelectedRow} />
        )}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding:'3rem', textAlign:'center', color:T.txt3 }}>
            Nessun dato disponibile per il periodo selezionato.
          </div>
        )}
      </div>

      {selectedRow && (
        <SidePanel candidate={selectedRow} allRows={rows} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}
